import { IDeveloperBillingRestClient } from "./i-developer-billing-rest.client";
import { IBillingCustomer } from "../../models/i-billing-customer";
import { IPrice } from "../../models/i-price";
import { IBillingConfig } from "../../models/i-billing-config";
import { IPaymentMethod } from "../../models/i-payment-method";
import { ISetupIntent } from "../../models/i-setup-intent";
import { ISubscription } from "../../models/i-subscription";
import { IInvoice } from "../../models/i-invoice";
import { IConnectedAccount } from "../../models/i-connected-account";
import { IAccountLink } from "../../models/i-account-link";
import { IDashboardLink } from "../../models/i-dashboard-link";
import { ICreateSubscriptionForm } from "./forms/i-create-subscription-form";
import { ICancelSubscriptionForm } from "./forms/i-cancel-subscription-form";
import { IOnboardConnectForm } from "./forms/i-onboard-connect-form";
import { HostUtil } from "../../utils/host-util";
import { firstValueFrom, Observable } from "rxjs";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { ARestClient, IOkResponse } from "@zaeper/communication-lib";
import { Injectable } from "@angular/core";

@Injectable()
export class DeveloperBillingRestClient extends ARestClient implements IDeveloperBillingRestClient {
	constructor(private readonly _httpClient: HttpClient) {
		super();
	}

	getBillingConfig(): Promise<IBillingConfig> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/billing/config");

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IBillingConfig>);
	}

	listPaymentMethods(): Promise<IPaymentMethod[]> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/developer/billing/payment-methods");

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IPaymentMethod[]>);
	}

	createSetupIntent(): Promise<ISetupIntent> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/developer/billing/payment-methods/setup");

		return firstValueFrom(this._httpClient.post(endpointUrl, {}, { withCredentials: true }) as Observable<ISetupIntent>);
	}

	setDefaultPaymentMethod(stripePaymentMethodId: string): Promise<IOkResponse> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/developer/billing/payment-methods/${stripePaymentMethodId}/default`);

		return firstValueFrom(this._httpClient.put(endpointUrl, {}, { withCredentials: true }) as Observable<IOkResponse>);
	}

	detachPaymentMethod(stripePaymentMethodId: string): Promise<IOkResponse> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), `v1/developer/billing/payment-methods/${stripePaymentMethodId}`);

		return firstValueFrom(this._httpClient.delete(endpointUrl, { withCredentials: true }) as Observable<IOkResponse>);
	}

	async getCustomer(): Promise<IBillingCustomer | null> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/developer/billing/customer");

		try {
			return await firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IBillingCustomer>);
		} catch (e) {
			if (e instanceof HttpErrorResponse && e.status === 404) {
				return null;
			}
			throw e;
		}
	}

	createCustomer(country: string): Promise<IBillingCustomer> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/developer/billing/customer");

		return firstValueFrom(this._httpClient.post(endpointUrl, { country }, { withCredentials: true }) as Observable<IBillingCustomer>);
	}

	getLicensePrice(country: string | null): Promise<IPrice[]> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/developer/billing/license/price");
		const params: Record<string, string> = country ? { country } : {};

		return firstValueFrom(this._httpClient.get(endpointUrl, { params, withCredentials: true }) as Observable<IPrice[]>);
	}

	getLicense(): Promise<ISubscription[]> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/developer/billing/license");

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<ISubscription[]>);
	}

	subscribeLicense(form: ICreateSubscriptionForm): Promise<ISubscription> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/developer/billing/license/subscribe");

		return firstValueFrom(this._httpClient.post(endpointUrl, form, { withCredentials: true }) as Observable<ISubscription>);
	}

	syncLicense(stripeSubscriptionId: string): Promise<ISubscription> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/developer/billing/license/sync");

		return firstValueFrom(this._httpClient.post(endpointUrl, { stripeSubscriptionId }, { withCredentials: true }) as Observable<ISubscription>);
	}

	cancelLicense(form: ICancelSubscriptionForm): Promise<IOkResponse> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/developer/billing/license/cancel");

		return firstValueFrom(this._httpClient.post(endpointUrl, form, { withCredentials: true }) as Observable<IOkResponse>);
	}

	listInvoices(): Promise<IInvoice[]> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/developer/billing/invoices");

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IInvoice[]>);
	}

	onboardConnect(form: IOnboardConnectForm): Promise<IAccountLink> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/developer/connect/onboard");

		return firstValueFrom(this._httpClient.post(endpointUrl, form, { withCredentials: true }) as Observable<IAccountLink>);
	}

	getConnectStatus(): Promise<IConnectedAccount> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/developer/connect/status");

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IConnectedAccount>);
	}

	getConnectDashboardLink(): Promise<IDashboardLink> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getBillingApiEndpointUrl(), "v1/developer/connect/dashboard-link");

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IDashboardLink>);
	}
}
